import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { emailWithTldValidator } from 'src/app/validators/email-tld.validator';
import { cpfValidator, cpfCnpjValidator } from 'src/app/validators/cpf-cnpj.validator';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { combineLatest, Observable } from 'rxjs';
import { IPatient } from 'src/app/models/patient.model';
import { ICountriesDDI } from 'src/app/models/countries-ddi.model';
import { countriesDdi } from 'src/app/models/countries_ddi';
import { IError } from 'src/app/models/error.model';
import { PatientsService } from 'src/app/services/patients.service';
import { ModulesService } from 'src/app/services/modules.service';
import { AuthService } from 'src/app/services/auth.service';
import { setWarningText } from 'src/app/reducers/warning-popup.reducer';
import { IModule } from 'src/app/models/module.model';
import { DropdownOption } from '../../components/custom-dropdown/custom-dropdown.component';

@Component({
  selector: 'app-patient-form-v2',
  templateUrl: './patient-form.component.html',
  styleUrls: ['./patient-form.component.sass']
})
export class PatientFormV2Component implements OnInit {
  loading = false;
  patientForm!: FormGroup;
  
  countriesDdi = countriesDdi;
  filteredPhoneCountries = countriesDdi;
  filteredGuardianPhoneCountries = countriesDdi;
  isDropdownPhoneOpen = false;
  isDropdownGuardianPhoneOpen = false;

  // Cálculo de idade para exibir dados do responsável
  patientAge: number | null = null;
  isMinor = false;

  // Paciente modelo feature flag
  showModelPatientCheckbox = false;

  // Opções para dropdowns
  maritalStatusOptions: DropdownOption[] = [
    { value: 'Solteiro(a)', label: 'Solteiro(a)' },
    { value: 'Casado(a)', label: 'Casado(a)' },
    { value: 'Separado(a)', label: 'Separado(a)' },
    { value: 'Divorciado(a)', label: 'Divorciado(a)' },
    { value: 'Viúvo(a)', label: 'Viúvo(a)' }
  ];

  // Opções de países para dropdown
  countryOptions: DropdownOption[] = countriesDdi
    .filter(c => c.pais)
    .map(c => ({ value: c.pais!, label: c.pais! }));

  // Menu de seleção
  activeSection = 'info';

  // Módulo dinâmico
  viewModules$: Observable<{ moduleName: string; moduleNamePascalCase: string; moduleNameInSingular: string; }>;

  constructor(
    private fb: FormBuilder,
    private patientsService: PatientsService,
    private router: Router,
    private store: Store<{ warningPopup: IError }>,
    public modulesService: ModulesService,
    private authService: AuthService
  ) {
    this.viewModules$ = combineLatest({
      moduleName: this.modulesService.moduleName$,
      moduleNamePascalCase: this.modulesService.moduleNamePascalCase$,
      moduleNameInSingular: this.modulesService.moduleNameInSingular$
    });
  }

  ngOnInit(): void {
    this.initForm();
    this.evaluateModelPatientVisibility();
  }

  // Avalia se deve mostrar o checkbox baseado no módulo e feature da clínica
  private evaluateModelPatientVisibility(): void {
    const isPacientesModule = this.modulesService.moduleName === 'PACIENTES';
    const hasPermission = this.authService.getPatientModelPermission();
    this.showModelPatientCheckbox = isPacientesModule && hasPermission;
  }

  initForm(): void {
    this.patientForm = this.fb.group({
      name: ['', [Validators.required]],
      email: ['', [emailWithTldValidator()]],
      phone: [''],
      phoneCountryCode: ['Brasil'],
      birthDate: [''],
      profession: [''],
      maritalStatus: [''],
      isForeigner: [false],
      isModelPatient: [false],
      // Campos adicionais
      instagramAccount: [''],
      rg: [''],
      cpf: ['', [this.modulesService.moduleName === 'ALUNOS' ? cpfCnpjValidator() : cpfValidator()]],
      // Campos do responsável legal
      guardianName: [''],
      guardianCpf: ['', [cpfValidator()]],
      guardianPhone: [''],
      guardianPhoneCountryCode: ['Brasil'],
      // Endereço
      zipCode: [''],
      street: [''],
      number: [''],
      complement: [''],
      neighborhood: [''],
      city: [''],
      state: [''],
      country: ['']
    });
  }

  // Dropdown de país do telefone
  onClickPhoneCountryButton(): void {
    this.isDropdownPhoneOpen = !this.isDropdownPhoneOpen;
    if (this.isDropdownPhoneOpen) {
      this.filteredPhoneCountries = this.countriesDdi;
    }
  }

  filterPhoneCountries(event: Event): void {
    const searchTerm = (event.target as HTMLInputElement).value.toLowerCase();
    this.filteredPhoneCountries = this.countriesDdi.filter(country => 
      (country.pais?.toLowerCase() || '').includes(searchTerm) || 
      (country.ddi || '').includes(searchTerm)
    );
  }

  onClickSelectPhoneCountry(country: ICountriesDDI): void {
    this.patientForm.get('phoneCountryCode')?.setValue(country.pais);
    this.isDropdownPhoneOpen = false;
  }

  getSelectedPhoneCountry(): ICountriesDDI {
    const countryCode = this.patientForm.get('phoneCountryCode')?.value;
    return this.countriesDdi.find(c => c.pais === countryCode) || this.getDefaultCountryDdiObject();
  }

  getDefaultCountryDdiObject(): ICountriesDDI {
    return {
      pais: 'Brasil',
      img: 'https://upload.wikimedia.org/wikipedia/commons/thumb/0/05/Flag_of_Brazil.svg/22px-Flag_of_Brazil.svg.png',
      ddi: '+55'
    } as ICountriesDDI;
  }

  // Formatação de data
  onBirthDateInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    let value = input.value.replace(/\D/g, '');
    
    if (value.length >= 2) {
      value = value.substring(0, 2) + '/' + value.substring(2);
    }
    if (value.length >= 5) {
      value = value.substring(0, 5) + '/' + value.substring(5, 9);
    }
    
    input.value = value;
    this.patientForm.get('birthDate')?.setValue(value, { emitEvent: false });

    // Atualiza a idade e verifica se é menor de idade
    this.updateMinorStatus(value);
  }

  /**
   * Atualiza o status de menor de idade baseado na data de nascimento
   */
  private updateMinorStatus(birthDateString: string): void {
    if (birthDateString && birthDateString.length === 10) {
      this.patientAge = this.calculateAge(birthDateString);
      this.isMinor = this.patientAge !== null && this.patientAge < 18;
    } else {
      this.patientAge = null;
      this.isMinor = false;
    }
  }

  /**
   * Calcula a idade do paciente baseado na data de nascimento
   */
  private calculateAge(birthDateString: string | null | undefined): number | null {
    if (!birthDateString) {
      return null;
    }

    let birthDate: Date;
    if (birthDateString.includes('-')) {
      const parts = birthDateString.split('-');
      birthDate = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
    } else if (birthDateString.includes('/')) {
      const parts = birthDateString.split('/');
      birthDate = new Date(Number(parts[2]), Number(parts[1]) - 1, Number(parts[0]));
    } else {
      return null;
    }

    if (isNaN(birthDate.getTime())) {
      return null;
    }

    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age < 0 ? 0 : age;
  }

  // ========== CPF/CNPJ INPUT ==========
  onCpfCnpjInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    let value = input.value.replace(/\D/g, '');

    if (value.length <= 11) {
      // CPF: 000.000.000-00
      if (value.length > 3) value = value.substring(0, 3) + '.' + value.substring(3);
      if (value.length > 7) value = value.substring(0, 7) + '.' + value.substring(7);
      if (value.length > 11) value = value.substring(0, 11) + '-' + value.substring(11, 13);
    } else {
      // CNPJ: 00.000.000/0000-00
      if (value.length > 2) value = value.substring(0, 2) + '.' + value.substring(2);
      if (value.length > 6) value = value.substring(0, 6) + '.' + value.substring(6);
      if (value.length > 10) value = value.substring(0, 10) + '/' + value.substring(10);
      if (value.length > 15) value = value.substring(0, 15) + '-' + value.substring(15, 17);
    }

    input.value = value;
    this.patientForm.get('cpf')?.setValue(value, { emitEvent: false });
  }

  // ========== GUARDIAN PHONE COUNTRY DROPDOWN ==========
  onClickGuardianPhoneCountryButton(): void {
    this.isDropdownGuardianPhoneOpen = !this.isDropdownGuardianPhoneOpen;
    if (this.isDropdownGuardianPhoneOpen) {
      this.filteredGuardianPhoneCountries = this.countriesDdi;
    }
  }

  filterGuardianPhoneCountries(event: Event): void {
    const searchTerm = (event.target as HTMLInputElement).value.toLowerCase();
    this.filteredGuardianPhoneCountries = this.countriesDdi.filter(country => 
      (country.pais?.toLowerCase() || '').includes(searchTerm) || 
      (country.ddi || '').includes(searchTerm)
    );
  }

  onClickSelectGuardianPhoneCountry(country: ICountriesDDI): void {
    this.patientForm.get('guardianPhoneCountryCode')?.setValue(country.pais);
    this.isDropdownGuardianPhoneOpen = false;
  }

  getSelectedGuardianPhoneCountry(): ICountriesDDI {
    const countryCode = this.patientForm.get('guardianPhoneCountryCode')?.value;
    return this.countriesDdi.find(c => c.pais === countryCode) || this.getDefaultCountryDdiObject();
  }

  // ========== CEP / ZIPCODE ==========
  onCepInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const isForeigner = this.patientForm.get('isForeigner')?.value;

    if (isForeigner) {
      // Para estrangeiros, aceita qualquer formato (letras, números, espaços, hífens)
      this.patientForm.get('zipCode')?.setValue(input.value, { emitEvent: false });
      return;
    }

    // Para brasileiros, aplica máscara de CEP (00000-000)
    let value = input.value.replace(/\D/g, '');

    if (value.length > 5) {
      value = value.substring(0, 5) + '-' + value.substring(5, 8);
    }

    input.value = value;
    this.patientForm.get('zipCode')?.setValue(value, { emitEvent: false });
  }

  cepOnChange(): void {
    const isForeigner = this.patientForm.get('isForeigner')?.value;
    if (isForeigner) return;

    const cep = this.patientForm.get('zipCode')?.value?.replace(/\D/g, '');
    if (!cep || cep.length !== 8) return;

    // Busca CEP via ViaCEP API
    fetch(`https://viacep.com.br/ws/${cep}/json/`)
      .then(response => response.json())
      .then(data => {
        if (!data.erro) {
          this.patientForm.patchValue({
            street: data.logradouro || '',
            neighborhood: data.bairro || '',
            city: data.localidade || '',
            state: data.uf || '',
            country: 'Brasil'
          });
        }
      })
      .catch(error => {
        console.error('Erro ao buscar CEP:', error);
      });
  }

  private getModuleRouteBase(): string[] {
    const moduleName = (this.modulesService.moduleName || '').toString().toUpperCase();
    if (moduleName === 'PACIENTES') {
      return ['main', 'v2', 'patients'];
    }
    return ['main', 'v2', 'modules', moduleName.toLowerCase()];
  }

  onCancel(): void {
    const base = this.getModuleRouteBase();
    this.router.navigate(base);
  }

  onSubmit(): void {
    if (this.patientForm.get('name')?.invalid) {
      this.patientForm.markAllAsTouched();
      this.store.dispatch(setWarningText('Preencha o campo Nome do Paciente.'));
      return;
    }

    // Verifica se o e-mail está com formato inválido (se foi preenchido)
    if (this.patientForm.get('email')?.invalid) {
      this.patientForm.markAllAsTouched();
      this.store.dispatch(setWarningText('Insira um e-mail válido (ex: nome@dominio.com).'));
      return;
    }

    // Verifica se o CPF do responsável está com formato inválido (se foi preenchido)
    if (this.patientForm.get('guardianCpf')?.invalid) {
      this.patientForm.markAllAsTouched();
      this.store.dispatch(setWarningText('Insira um CPF válido para o responsável.'));
      return;
    }

    this.loading = true;
    const formValue = this.patientForm.value;

    // Converte data de DD/MM/YYYY para YYYY-MM-DD
    let birthDateFormatted = '';
    if (formValue.birthDate) {
      const parts = formValue.birthDate.split('/');
      if (parts.length === 3) {
        birthDateFormatted = `${parts[2]}-${parts[1]}-${parts[0]}`;
      }
    }

  var onlyNumbersPhoneNumber = (formValue.phone || '').toString().replace(/[^\d]+/g, '');
  const isForeigner = formValue.isForeigner || false;

   const patientRequest: IPatient = {
      full_name: (formValue.name || '').trim(),
      email: (formValue.email || '').trim().toLowerCase(),
      phone: onlyNumbersPhoneNumber || '',
      phone_country_code: (formValue.phoneCountryCode || 'Brasil').trim(),
      birth_date: this.patientForm.controls['birthDate_iso']?.value || '',
      profession: (formValue.profession || '').trim(),
      marital_status: (formValue.maritalStatus || '').trim(),
      is_foreigner: isForeigner,
      is_model_patient: this.showModelPatientCheckbox ? !!formValue.isModelPatient : false,
      // Campos adicionais
      instagram_account: (formValue.instagramAccount || '').trim(),
      rg: (formValue.rg || '').trim(),
      cpf: (formValue.cpf || '').trim(),
      // Dados do responsável legal
      guardian_name: formValue.guardianName?.trim() || '',
      guardian_cpf: formValue.guardianCpf?.trim() || '',
      guardian_phone: formValue.guardianPhone?.trim() || '',
      guardian_phone_country_code: formValue.guardianPhoneCountryCode || 'Brasil',
      module: {
        id: this.modulesService.moduleId,
        description: this.modulesService.moduleName
      } as IModule,
      address: {
        street: (formValue.street || '').trim(),
        number: (formValue.number || '').trim(),
        complement: (formValue.complement || '').trim(),
        neighborhood: (formValue.neighborhood || '').trim(),
        city: (formValue.city || '').trim(),
        state: (formValue.state || '').trim(),
        zip_code: formValue.isForeigner
          ? (formValue.zipCode || '').trim()
          : (formValue.zipCode || '').replace(/[^\d]+/g, ''),
        country: (formValue.country || 'Brasil').trim()
      }
    };


    

    this.patientsService.create(patientRequest).subscribe({
      next: (response) => {
        this.loading = false;
        const base = this.getModuleRouteBase();
        const patientId = (response as IPatient)?.id;
        // Redireciona para a tela de edição do paciente recém-criado
        this.router.navigateByUrl('/main/v2', { skipLocationChange: true }).then(() => {
          this.router.navigate([...base, patientId, 'edit']);
        });
      },
      error: (error) => {
        console.error('Erro ao criar paciente:', error);
        this.loading = false;
      }
    });
  }
}
